import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/integrations/api/client";

export const Route = createFileRoute("/_authenticated/reports/warranty-expiry")({
  component: WarrantyExpiryReport,
});

function WarrantyExpiryReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "warranty-expiry", currentBranchId],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (currentBranchId) p.set("branch", currentBranchId);
      const res = await apiGet(`/reports/warranty-expiry/?${p.toString()}`);
      return res as any[];
    }
  });

  const exportRows = useMemo(() => () => (data ?? []).map((r) => ({
    "Asset Code": r.asset_code,
    "Name": r.name,
    "Department": r.department,
    "Supplier": r.supplier,
    "Warranty Expiry": r.warranty_expiry,
  })), [data]);

  return (
    <ReportShell
      id="R-10"
      title="Warranty Expiry List"
      description="Assets and contracts with warranty windows nearing expiry."
      filters={filters}
      onFiltersChange={setFilters}
      onExport={exportRows}
      exportFilename="Warranty_Expiry_List"
      allowedFilters={["branchId"]}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Warranty Expiry</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No data found</TableCell></TableRow>
                )}
                {data?.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.asset_code}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.department}</TableCell>
                    <TableCell>{r.supplier || "—"}</TableCell>
                    <TableCell>{r.warranty_expiry}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
